import { useAuthentication } from '@connected/api-client/hooks/use-authentication';
import { portaalQueryOptions } from '@connected/portaal-sdk/queries/options';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Outlet, useLocation } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@/components/breadcrumb';

export const Route = createFileRoute(
    '/_public/customer/locations/$locationId/electrical/projects/$projectId/_details/_reports',
)({
    component: RouteComponent,
});

function RouteComponent() {
    const auth = useAuthentication();
    const { t } = useTranslation('customer-module-electrical');
    const params = Route.useParams();
    const location = useLocation();

    const objectQuery = useQuery({
        ...portaalQueryOptions.portal.objects.view({
            ObjectId: params.locationId,
        }),
        enabled: !!auth.isAuthenticated,
    });

    const projectQuery = useQuery({
        ...portaalQueryOptions.electrical.project.view({
            ElectricalProjectId: params.projectId,
        }),
        enabled: !auth.isAuthenticated,
    });
    // Extract current section from URL
    const pathSegments = location.pathname.split('/');
    const installationType = pathSegments[pathSegments.length - 4];
    const installationId = pathSegments[pathSegments.length - 3];
    const notificationObservationType = pathSegments[pathSegments.length - 2];
    const notificationObservationId = pathSegments[pathSegments.length - 1];

    // Conditional queries based on section
    const { data: distributionBoardData } = useQuery({
        ...portaalQueryOptions.electrical.distributionBoards.view({ ElectricalProjectDistributionBoardId: installationId || '' }),
        enabled: !!((installationType === 'control-boxes' || installationType === 'distribution-boards') && installationId),
    });

    const { data: finalCircuitData } = useQuery({
        ...portaalQueryOptions.electrical.finalcircuits.view({ ElectricalProjectFinalCircuitId: installationId || '' }),
        enabled: !!((installationType === 'inverters' || installationType === 'machines' || installationType === 'other') && installationId),
    });

    // Get item name based on available data
    const getItemName = () => {
        if (distributionBoardData?.DistributionBoard?.Identifier) {
            return distributionBoardData.DistributionBoard.Identifier;
        }
        if (finalCircuitData?.FinalCircuit?.Identifier) {
            return finalCircuitData.FinalCircuit.Identifier;
        }
        return '';
    };

    // Get dynamic params based on current section
    const getDynamicParams = () => {
        const parameterName = notificationObservationType === 'notifications' ? 'notificationId' : 'observationId';
        const baseParams = {
            locationId: params.locationId,
            projectId: params.projectId,
            [parameterName]: notificationObservationId,
        };

        switch (installationType) {
            case 'control-boxes':
                return { ...baseParams, controlboxId: installationId };
            case 'distribution-boards':
                return { ...baseParams, distributionId: installationId };
            default:
                return baseParams;
        }
    };

    // Get dynamic route path based on current section
    const getDynamicRoute = () => {
        const basePath = '/customer/locations/$locationId/electrical/projects/$projectId';
        const parameterName = notificationObservationType === 'notifications' ? 'notificationId' : 'observationId';

        switch (installationType) {
            case 'control-boxes':
                return `${basePath}/final-circuit/$controlboxId/${notificationObservationType}/$${parameterName}`;
            case 'distribution-boards':
                return `${basePath}/distribution-boards/$distributionId/${notificationObservationType}/$${parameterName}`;
            default:
                return basePath;
        }
    };

    return (
        <div className="max-w-screen-2xl flex flex-col flex-1 p-8 pb-0 overflow-y-auto h-full">
            <Breadcrumb
                items={[
                    {
                        name: auth.isAuthenticated
                            ? objectQuery.data?.Object?.Name || t('module-electrical.projects.title')
                            : projectQuery.data?.Project?.ProjectObject?.Name || t('module-electrical.projects.title'),
                        to: '/customer/locations/$locationId',
                        params: { locationId: params.locationId },
                    },
                    {
                        name: t('module-electrical.name'),
                        subcategory: {
                            name: t(`module-electrical.${installationType}.title`),
                            to: `/customer/locations/$locationId/electrical/projects/$projectId/${installationType}`,
                            params: {
                                locationId: params.locationId,
                                projectId: params.projectId,
                            },
                        },
                    },
                    ...(getItemName()
                        ? [{
                                name: getItemName(),
                                to: `/customer/locations/$locationId/electrical/projects/$projectId/${installationType}/${installationId}`,
                                params: { locationId: params.locationId, projectId: params.projectId },
                            }]
                        : []),
                    {
                        isActive: true,
                        name: t(`module-electrical.report.${notificationObservationType}.title`),
                        to: getDynamicRoute(),
                        params: getDynamicParams(),
                    },
                ]}
            />
            <Outlet />
        </div>
    );
}
